import { Field, RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Box from '@mui/material/Box';
import { ComponentProps } from 'lib/component-props';
import {
  StSiteMasterStaffAvatar,
  StSiteMasterAdvantagesContainer,
  StSiteMasterStaffCopy,
  StSiteMasterStaffList,
  StSiteMasterStaffMeta,
  StSiteMasterStaffName,
  StSiteMasterStaffProfile,
  StSiteMasterWhyChooseHeading,
  StSiteMasterWhyChooseSection,
} from 'components/SiteMaster/SiteMaster.styles';
import {
  getFieldValue,
  hasVisibleRichText,
  useSiteMasterRouteFields,
} from 'components/SiteMaster/utils';
import {
  formatPiDisplayName,
  formatPiMetaLine,
  getInitialsFromName,
  getSitePiItems,
} from './SiteContactDetailHelper';

type SiteContactDetailProps = ComponentProps & {
  fields: {
    sectionTitle?: Field<string>;
    siteAdvantagesText?: Field<string>;
  };
};

const SiteContactDetail = (props: SiteContactDetailProps): JSX.Element | null => {
  const routeFields = useSiteMasterRouteFields();
  const piItems = getSitePiItems(routeFields);
  const hasAdvantages = hasVisibleRichText(getFieldValue(routeFields.siteAdvantages));
  const hasPis = piItems.length > 0;

  if (!hasPis && !hasAdvantages) {
    return null;
  }

  const sectionTitle = getFieldValue(props.fields.sectionTitle).trim();
  const advantagesHeading = getFieldValue(props.fields.siteAdvantagesText).trim();

  return (
    <StSiteMasterWhyChooseSection>
      {sectionTitle && (
        <StSiteMasterWhyChooseHeading>
          <Text field={props.fields.sectionTitle} tag="h2" />
          <span className="underline-bar" aria-hidden />
        </StSiteMasterWhyChooseHeading>
      )}

      {hasPis && (
        <StSiteMasterStaffList>
          {piItems.map((pi, index) => {
            const name = getFieldValue(pi.fields?.name).trim();
            const displayName = formatPiDisplayName(pi);
            const metaLine = formatPiMetaLine(pi);
            const initials = getInitialsFromName(name);

            return (
              <StSiteMasterStaffProfile key={pi.id ?? `${name}-${index}`}>
                {initials && (
                  <StSiteMasterStaffAvatar aria-hidden>{initials}</StSiteMasterStaffAvatar>
                )}
                {(displayName || metaLine) && (
                  <StSiteMasterStaffCopy>
                    {displayName && <StSiteMasterStaffName>{displayName}</StSiteMasterStaffName>}
                    {metaLine && <StSiteMasterStaffMeta>{metaLine}</StSiteMasterStaffMeta>}
                  </StSiteMasterStaffCopy>
                )}
              </StSiteMasterStaffProfile>
            );
          })}
        </StSiteMasterStaffList>
      )}

      {hasAdvantages && (
        <StSiteMasterAdvantagesContainer>
          {advantagesHeading && (
            <Box className="advantages-heading" component="p">
              <Text field={props.fields.siteAdvantagesText} />
            </Box>
          )}
          <Box className="advantages-body">
            <RichText field={routeFields.siteAdvantages} tag="div" />
          </Box>
        </StSiteMasterAdvantagesContainer>
      )}
    </StSiteMasterWhyChooseSection>
  );
};

export default SiteContactDetail;
